import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useSignupApi } from '../api';
import { isTransientFailure } from '../helpers/apiErrors';
import { signupAllowedSteps, signupOnboardingRoute, signupStepFallbackRoute } from '../helpers/resumeRoute';
import { SignupFunnelStepPath, SignupState } from '../types';

export type SignupStepGuardResult = {
  /**
   * Null while the answer is on its way, while the client is being moved off a step that is not theirs, and
   * while all that is held is a read made about a different step.
   */
  state: SignupState | null;
  /** Callers must render nothing of their step while this lasts, or a step the funnel never reached shows. */
  checking: boolean;
  /**
   * The funnel could not be read, for a reason that may well pass. Nothing of the step may render: a screen
   * built from a funnel it does not have shows numbers and names that are not the client's.
   */
  unavailable: boolean;
  retry: () => void;
};

/**
 * Whether the client belongs on the funnel screen they asked for, answered before the screen does anything.
 * Left to the API, the refusal arrives written for whoever called the endpoint.
 *
 * The funnel is handed back rather than kept private: every screen behind this guard needs it anyway, which
 * is what keeps the four of them to one read instead of one each.
 */
export const useSignupStepGuard = (step: SignupFunnelStepPath): SignupStepGuardResult => {
  const companyId = useSelector(getCurrentCompanyId);
  const api = useSignupApi(companyId ?? undefined);
  const navigate = useNavigate();

  /**
   * The funnel and the step it was read for, never the funnel alone: the clearing below happens in an effect,
   * one render after the new step arrives, and what a screen is handed on that render is the whole of what it
   * acts on when it mounts.
   */
  const [read, setRead] = useState<{ key: string; data: SignupState } | null>(null);
  const [reading, setReading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [attempt, setAttempt] = useState(0);

  /**
   * Which read the screen is holding, in place of the effect's dependencies: those are rebuilt on every render
   * of the host, so a rebuild would become a second read. Nothing cancels a request either, so this is also
   * what an arriving answer is checked against — one for a company since switched away from must not land.
   */
  const askedFor = useRef<string | null>(null);

  /**
   * Two ways to get this wrong, both stranding every screen behind the guard on its spinner. Not cleared by the
   * effect's cleanup, which re-runs on rebuilt dependencies and would abandon a read `askedFor` already counts
   * as made; armed on mount rather than at declaration, because StrictMode mounts one instance twice.
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const retry = useCallback(() => {
    setUnavailable(false);
    setReading(true);
    setAttempt(current => current + 1);
  }, []);

  useEffect(() => {
    if (companyId == null) {
      // Released rather than held: the screens render their own missing-company notice.
      setReading(false);
      return;
    }
    /*
      The step belongs in the key because one instance of this hook serves all four screens: React Router swaps
      the `step` prop rather than the element, so the refs above outlive the step they were read for. Keyed on
      the company alone, the payment screen was handed the funnel as it looked on the number picker — without
      the number the client had just chosen on it.
    */
    const readAbout = `${companyId}:${step}`;
    const readFor = `${readAbout}:${attempt}`;
    if (askedFor.current === readFor) return;
    askedFor.current = readFor;

    api.getCompanyState()
      .then(({ data }) => {
        if (!mounted.current || askedFor.current !== readFor) return;

        if (!signupAllowedSteps(data).includes(step)) {
          navigate(signupStepFallbackRoute(data, companyId), { replace: true });
          // The wait deliberately stays on: releasing it renders one frame of the screen being left.
          return;
        }

        setRead({ key: readAbout, data: data });
        setReading(false);
      })
      .catch((error: unknown) => {
        if (!mounted.current || askedFor.current !== readFor) return;

        // A definitive answer means there is no funnel to place this client in — every company the wizard
        // created answers 404 here — and nobody else will move them: `AuthContext` leaves funnel paths alone.
        // Left standing, they would meet a step whose every call the API refuses.
        if (!isTransientFailure(error)) {
          navigate(signupOnboardingRoute(companyId), { replace: true });
          return;
        }

        // A client sitting on their own step must not be sent to the wizard by a timeout.
        setUnavailable(true);
        setReading(false);
      });
  }, [api, attempt, companyId, navigate, step]);

  const readAbout = companyId == null ? null : `${companyId}:${step}`;
  const state = read != null && read.key === readAbout ? read.data : null;

  return {
    state: state,
    /*
      Derived rather than the flag alone, which is only raised by the effect and so is still down on the render
      that brought the new step in. Without a company there is nothing to wait for — the screens render their
      own notice — and `unavailable` is an answer rather than a wait.
    */
    checking: reading || (readAbout != null && state == null && !unavailable),
    unavailable: unavailable,
    retry: retry,
  };
};
